/**
 * SECCIÓN I — Sub-pestaña "Estatus de Almacenes" en Soporte PEMEX.
 * Multi-ubicación (Terrestre / Costera / Barco / Plataforma) con 3 cards (RP, RME, RSU) cada una.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { Modal } from "./ui";
import {
  Download, Edit2, Loader2, Plus, Save, Trash2, X, AlertTriangle, MapPin,
} from "lucide-react";

const LOCATION_TYPES = ["Instalación Terrestre", "Instalación Costera", "Barco", "Plataforma"];
const LOCATION_ICONS = {
  "Instalación Terrestre": "🏭",
  "Instalación Costera": "🏗",
  "Barco": "🚢",
  "Plataforma": "🛢",
};
const TYPE_META = {
  RP: { color: "#FF6D00", label: "RP" },
  RME: { color: "#1565C0", label: "RME" },
  RSU: { color: "#2E7D32", label: "RSU" },
};
const STATUS_BADGE = {
  green: { label: "✓ DISPONIBLE", bg: "rgba(0,200,83,0.18)", fg: "#00C853" },
  yellow: { label: "⚠ ATENCIÓN", bg: "rgba(255,214,0,0.22)", fg: "#B07A00" },
  red: { label: "🔴 LLENO", bg: "rgba(213,0,0,0.20)", fg: "#D50000" },
};

function ProgressBar({ pct, color = "#1565C0" }) {
  const safe = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div style={{ position: "relative", background: "var(--bg-2)", height: 8, borderRadius: 4, overflow: "hidden", border: "1px solid var(--border-2)" }}>
      <div style={{ width: `${safe}%`, height: "100%", background: color, transition: "width 200ms" }} />
    </div>
  );
}

function StatusBadge({ color }) {
  const s = STATUS_BADGE[color] || STATUS_BADGE.green;
  return (
    <span style={{ background: s.bg, color: s.fg, border: `1px solid ${s.fg}55`, padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>
      {s.label}
    </span>
  );
}

export default function PemexOffshoreWarehouse() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("Todas");
  const [showCreate, setShowCreate] = useState(false);
  const [renaming, setRenaming] = useState(null); // location object
  const [toast, setToast] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/offshore-warehouse/locations");
      setLocations(r.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(""), 2500); }

  async function deleteLocation(loc) {
    if (!window.confirm(`¿Eliminar la ubicación "${loc.location_name}" y sus 3 almacenes (RP/RME/RSU)?`)) return;
    try {
      await api.delete(`/offshore-warehouse/locations/${loc.location_id}`);
      showToast(`Ubicación ${loc.location_name} eliminada`);
      await reload();
    } catch (e) {
      alert("Error eliminando: " + (e?.response?.data?.detail || e.message));
    }
  }

  async function downloadPdf(location_id) {
    try {
      const url = location_id
        ? `/offshore-warehouse/report/pdf?location_id=${encodeURIComponent(location_id)}`
        : "/offshore-warehouse/report/pdf";
      const res = await api.post(url, {}, { responseType: "blob" });
      const blob = new Blob([res.data]);
      const u = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = location_id ? `almacenes_${location_id.slice(0, 8)}.pdf` : "almacenes_pemex.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(u), 1000);
      showToast("Reporte PDF descargado");
    } catch (e) {
      alert("Error PDF: " + (e?.response?.data?.detail || e.message));
    }
  }

  const filtered = filterType === "Todas"
    ? locations
    : locations.filter((l) => l.location_type === filterType);

  return (
    <div data-testid="pemex-offshore-panel" className="space-y-3">
      {/* Barra superior */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <h3 className="font-semibold" style={{ fontSize: 14 }}>Estatus de Almacenes por Ubicación</h3>
        <div className="flex gap-2 flex-wrap">
          <button className="btn" onClick={() => downloadPdf(null)} data-testid="ow-pdf-all">
            <Download size={14} /> Reporte PDF Todas
          </button>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)} data-testid="ow-add-location">
            <Plus size={14} /> Agregar Ubicación
          </button>
        </div>
      </div>

      {/* Filtro */}
      <div className="flex gap-1 flex-wrap" data-testid="ow-filter">
        {["Todas", ...LOCATION_TYPES].map((t) => (
          <button
            key={t}
            className={`btn btn-sm ${filterType === t ? "btn-primary" : ""}`}
            onClick={() => setFilterType(t)}
            data-testid={`ow-filter-${t.replace(/\s+/g, "-")}`}
          >
            {t !== "Todas" && (LOCATION_ICONS[t] || "")} {t}
          </button>
        ))}
      </div>

      {toast && (
        <div className="card" style={{ background: "rgba(0,200,83,0.10)", borderColor: "#00C853" }} data-testid="ow-toast">{toast}</div>
      )}

      {loading ? (
        <div className="card text-dim" data-testid="ow-loading">Cargando ubicaciones…</div>
      ) : filtered.length === 0 ? (
        <div className="card text-center" style={{ padding: 40 }} data-testid="ow-empty">
          <MapPin size={48} className="text-dim mx-auto" style={{ display: "block", margin: "0 auto 12px" }} />
          <div className="font-semibold" style={{ fontSize: 16, marginBottom: 4 }}>
            {locations.length === 0 ? "Sin ubicaciones registradas." : `Sin ubicaciones del tipo "${filterType}".`}
          </div>
          <div className="text-dim text-sm mb-3">Agrega tu primera instalación, barco o plataforma.</div>
          {locations.length === 0 && (
            <button className="btn btn-primary" onClick={() => setShowCreate(true)} data-testid="ow-add-first">
              <Plus size={14} /> Agregar Primera Ubicación
            </button>
          )}
        </div>
      ) : (
        filtered.map((loc) => (
          <LocationSection
            key={loc.location_id}
            loc={loc}
            onReload={reload}
            onRename={() => setRenaming(loc)}
            onDelete={() => deleteLocation(loc)}
            onDownloadPdf={() => downloadPdf(loc.location_id)}
            showToast={showToast}
          />
        ))
      )}

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Nueva Ubicación" size="md" testId="ow-modal-create">
        <CreateLocationForm
          onClose={() => setShowCreate(false)}
          onCreated={async (loc) => {
            setShowCreate(false);
            showToast(`Ubicación ${loc.location_name} creada.`);
            await reload();
          }}
        />
      </Modal>

      <Modal open={!!renaming} onClose={() => setRenaming(null)} title="Renombrar Ubicación" size="md" testId="ow-modal-rename">
        {renaming && (
          <RenameLocationForm
            loc={renaming}
            onClose={() => setRenaming(null)}
            onSaved={async () => {
              setRenaming(null);
              showToast("Ubicación actualizada.");
              await reload();
            }}
          />
        )}
      </Modal>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
function LocationSection({ loc, onReload, onRename, onDelete, onDownloadPdf, showToast }) {
  return (
    <div className="card" data-testid={`ow-section-${loc.location_id}`} style={{ borderLeft: "4px solid #1565C0" }}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span style={{ fontSize: 22 }}>{LOCATION_ICONS[loc.location_type] || "📍"}</span>
          <span className="font-semibold" style={{ fontSize: 15 }}>{loc.location_name}</span>
          <span style={{ background: "var(--bg-2)", border: "1px solid var(--border-2)", padding: "2px 8px", borderRadius: 4, fontSize: 11 }}>
            {loc.location_type}
          </span>
          {loc.any_alert_active && (
            <span style={{ background: "rgba(213,0,0,0.18)", color: "#D50000", border: "1px solid #D50000", padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600 }} data-testid={`ow-alert-${loc.location_id}`}>
              <AlertTriangle size={11} style={{ display: "inline", marginRight: 4 }} /> Alerta de capacidad
            </span>
          )}
        </div>
        <div className="flex gap-1">
          <button className="btn btn-sm" onClick={onRename} data-testid={`ow-rename-${loc.location_id}`}><Edit2 size={12} /> Renombrar</button>
          <button className="btn btn-sm" onClick={onDownloadPdf} data-testid={`ow-pdf-${loc.location_id}`}><Download size={12} /> PDF</button>
          <button className="btn btn-sm" style={{ color: "#D50000" }} onClick={onDelete} data-testid={`ow-delete-${loc.location_id}`}><Trash2 size={12} /> Eliminar</button>
        </div>
      </div>

      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(310px, 1fr))" }}>
        {["rp", "rme", "rsu"].map((k) => (
          <WarehouseCard
            key={k}
            locationId={loc.location_id}
            data={loc.warehouses[k]}
            onSaved={async () => { await onReload(); showToast?.(`${(loc.warehouses[k].warehouse_type)} actualizado`); }}
          />
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
function WarehouseCard({ locationId, data, onSaved }) {
  const wt = data.warehouse_type;
  const meta = TYPE_META[wt] || TYPE_META.RP;
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ ...data });
  const [saving, setSaving] = useState(false);
  const setF = (patch) => setForm({ ...form, ...patch });

  function start() { setForm({ ...data }); setEdit(true); }
  function cancel() { setEdit(false); }

  async function save() {
    setSaving(true);
    try {
      await api.put(`/offshore-warehouse/locations/${locationId}/${wt}`, form);
      setEdit(false);
      onSaved?.();
    } catch (e) {
      alert("Error guardando: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card" style={{ borderTop: `3px solid ${meta.color}` }} data-testid={`ow-card-${locationId}-${wt}`}>
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-2">
          <span style={{ background: meta.color, color: "white", padding: "2px 8px", borderRadius: 4, fontWeight: 600, fontSize: 13 }}>{meta.label}</span>
          <StatusBadge color={data.status_color} />
        </div>
        {edit ? (
          <div className="flex gap-1">
            <button className="btn btn-sm" onClick={cancel} disabled={saving}><X size={12} /></button>
            <button className="btn btn-sm btn-primary" onClick={save} disabled={saving} data-testid={`ow-save-${locationId}-${wt}`}>
              {saving ? <Loader2 size={12} className="spin" /> : <Save size={12} />} Guardar
            </button>
          </div>
        ) : (
          <button className="btn btn-sm" onClick={start} data-testid={`ow-edit-${locationId}-${wt}`}><Edit2 size={12} /> Editar</button>
        )}
      </div>

      <div className="text-xs text-dim">Responsable / Proveedor</div>
      {edit
        ? <input className="input" value={form.responsible_name || ""} onChange={(e) => setF({ responsible_name: e.target.value })} data-testid={`ow-resp-${locationId}-${wt}`} />
        : <div className="mb-2">{data.responsible_name || <span className="text-dim">Sin asignar</span>}</div>}

      <div className="grid grid-cols-2 gap-2 mt-2">
        <div>
          <div className="text-xs text-dim">Capacidad Total</div>
          {edit
            ? <div className="flex gap-1">
                <input className="input" type="number" value={form.total_capacity_volume ?? 0} onChange={(e) => setF({ total_capacity_volume: parseFloat(e.target.value || 0) })} data-testid={`ow-tcap-${locationId}-${wt}`} />
                <select className="input" value={form.volume_unit || "m³"} onChange={(e) => setF({ volume_unit: e.target.value })}>
                  {["m³", "kg", "ton"].map((u) => <option key={u}>{u}</option>)}
                </select>
              </div>
            : <div>{data.total_capacity_volume} {data.volume_unit}</div>}
        </div>
        <div>
          <div className="text-xs text-dim">Volumen Actual</div>
          {edit
            ? <input className="input" type="number" value={form.current_volume ?? 0} onChange={(e) => setF({ current_volume: parseFloat(e.target.value || 0) })} />
            : <div>{data.current_volume} {data.volume_unit}</div>}
        </div>
      </div>

      <div className="mt-2">
        <ProgressBar pct={data.occupied_volume_pct} color={STATUS_BADGE[data.status_color]?.fg || "#1565C0"} />
        <div className="text-xs text-dim mt-1">{data.occupied_volume_pct}% ocupado · {(data.available_volume || 0).toFixed(1)} {data.volume_unit} disponibles</div>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-2">
        <div>
          <div className="text-xs text-dim">Superficie Total</div>
          {edit
            ? <input className="input" type="number" value={form.total_surface_m2 ?? 0} onChange={(e) => setF({ total_surface_m2: parseFloat(e.target.value || 0) })} />
            : <div>{data.total_surface_m2} m²</div>}
        </div>
        <div>
          <div className="text-xs text-dim">Superficie Disponible</div>
          {edit
            ? <input className="input" type="number" value={form.available_surface_m2 ?? 0} onChange={(e) => setF({ available_surface_m2: parseFloat(e.target.value || 0) })} />
            : <div>{data.available_surface_m2} m²</div>}
        </div>
      </div>
      <div className="mt-2">
        <ProgressBar pct={data.occupied_surface_pct} color={STATUS_BADGE[data.status_color]?.fg || "#1565C0"} />
        <div className="text-xs text-dim mt-1">{data.occupied_surface_pct}% ocupada</div>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-2">
        <div>
          <div className="text-xs text-dim">YTD Acumulado</div>
          {edit
            ? <div className="flex gap-1">
                <input className="input" type="number" value={form.ytd_accumulated ?? 0} onChange={(e) => setF({ ytd_accumulated: parseFloat(e.target.value || 0) })} />
                <select className="input" value={form.ytd_unit || "m³"} onChange={(e) => setF({ ytd_unit: e.target.value })}>
                  {["m³", "kg", "ton"].map((u) => <option key={u}>{u}</option>)}
                </select>
              </div>
            : <div>{data.ytd_accumulated} {data.ytd_unit}</div>}
          <div className="text-xs text-dim">Volumen total administrado en el año</div>
        </div>
        <div>
          <div className="text-xs text-dim">Nivel de alerta</div>
          {edit
            ? <input className="input" type="number" min={0} max={100} value={form.alert_threshold_pct ?? 80} onChange={(e) => setF({ alert_threshold_pct: parseFloat(e.target.value || 0) })} />
            : <div>{data.alert_threshold_pct}%</div>}
        </div>
      </div>

      <div className="mt-2">
        <div className="text-xs text-dim">Tipo de residuo predominante</div>
        {edit
          ? <input className="input" value={form.dominant_waste_type || ""} onChange={(e) => setF({ dominant_waste_type: e.target.value })} />
          : <div>{data.dominant_waste_type || <span className="text-dim">—</span>}</div>}
      </div>

      <div className="mt-2">
        <div className="text-xs text-dim">Notas / Observaciones</div>
        {edit
          ? <textarea className="input" rows={2} value={form.notes || ""} onChange={(e) => setF({ notes: e.target.value })} />
          : <div className="text-sm">{data.notes || <span className="text-dim">—</span>}</div>}
      </div>

      <div className="text-xs text-dim mt-2" style={{ borderTop: "1px solid var(--border-2)", paddingTop: 6 }}>
        {data.updated_at
          ? <>Última actualización: {new Date(data.updated_at).toLocaleString("es-MX")}</>
          : "Sin datos registrados"}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
function CreateLocationForm({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!name.trim()) { alert("Nombre requerido"); return; }
    if (!type) { alert("Tipo requerido"); return; }
    setSaving(true);
    try {
      const r = await api.post("/offshore-warehouse/locations", { location_name: name.trim(), location_type: type });
      onCreated?.(r.data);
    } catch (e) {
      alert("Error creando: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-3" data-testid="ow-create-form">
      <div>
        <div className="text-xs text-dim mb-1">Nombre de la Ubicación *</div>
        <input
          className="input"
          placeholder="ej. Plataforma Akal-C, Barco Cormoran, Base Terminal Carmen"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="ow-create-name"
        />
      </div>
      <div>
        <div className="text-xs text-dim mb-1">Tipo de Ubicación *</div>
        <div className="grid grid-cols-2 gap-2">
          {LOCATION_TYPES.map((t) => (
            <button
              key={t}
              className={`btn ${type === t ? "btn-primary" : ""}`}
              style={{ padding: "16px 12px", fontSize: 13, display: "flex", flexDirection: "column", gap: 4, alignItems: "center" }}
              onClick={() => setType(t)}
              data-testid={`ow-create-type-${t.replace(/\s+/g, "-")}`}
            >
              <span style={{ fontSize: 26 }}>{LOCATION_ICONS[t]}</span>
              <span>{t}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-3">
        <button className="btn" onClick={onClose} disabled={saving}>Cancelar</button>
        <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="ow-create-save">
          {saving ? <Loader2 size={12} className="spin" /> : <Plus size={12} />} Crear Ubicación
        </button>
      </div>
    </div>
  );
}

function RenameLocationForm({ loc, onClose, onSaved }) {
  const [name, setName] = useState(loc.location_name);
  const [type, setType] = useState(loc.location_type);
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!name.trim()) { alert("Nombre requerido"); return; }
    setSaving(true);
    try {
      await api.patch(`/offshore-warehouse/locations/${loc.location_id}`, { location_name: name.trim(), location_type: type });
      onSaved?.();
    } catch (e) {
      alert("Error: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-3" data-testid="ow-rename-form">
      <div>
        <div className="text-xs text-dim mb-1">Nombre</div>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} data-testid="ow-rename-name" />
      </div>
      <div>
        <div className="text-xs text-dim mb-1">Tipo</div>
        <select className="input" value={type} onChange={(e) => setType(e.target.value)} data-testid="ow-rename-type">
          {LOCATION_TYPES.map((t) => <option key={t}>{t}</option>)}
        </select>
      </div>
      <div className="flex justify-end gap-2 mt-3">
        <button className="btn" onClick={onClose} disabled={saving}>Cancelar</button>
        <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="ow-rename-save">
          {saving ? <Loader2 size={12} className="spin" /> : <Save size={12} />} Guardar cambios
        </button>
      </div>
    </div>
  );
}
